import { Check } from 'lucide-react';
import { useAuth } from '@platform/auth-client';
import { Button, Dialog, Skeleton } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { COST_ROLE } from '../catalog/product.ts';
import { INVENTORY_WRITE_ROLE } from '../inventory/inventory.ts';
import { useActiveLocations } from '../inventory/useStockMatrix.ts';
import { CSV_FILES, FileDropZone } from '../FileDropZone.tsx';
import { usePageContext } from '../usePageContext.ts';
import { IMPORT_LOCALES, type ImportLocale } from './import-model.ts';
import { canCheck, MappingStep, ReportStep, ResultStep, TemplateButton } from './ImportSteps.tsx';
import styles from './ImportSheet.module.css';
import { useProductImport } from './useProductImport.ts';

export type ImportSheetProps = {
  api: TypedClient;
  shopLocales: readonly string[];
  onClose: () => void;
};

const shopLocaleOf = (locales: readonly string[]): ImportLocale =>
  locales
    .map((locale) => IMPORT_LOCALES.find((candidate) => candidate === locale))
    .find((locale) => locale !== undefined) ?? 'fr';

export const ImportSheet = ({ api, shopLocales, onClose }: ImportSheetProps) => {
  const { t, locale } = usePageContext();
  const auth = useAuth();
  const defaultLocale = shopLocaleOf(shopLocales);
  const permissions = {
    canSeeCost: auth.hasRole(COST_ROLE),
    canWriteStock: auth.hasRole(INVENTORY_WRITE_ROLE),
  };
  const depots = useActiveLocations(api);
  const flow = useProductImport(api, defaultLocale, permissions);
  const { state } = flow;

  const body = () => {
    if (state.step === 'checking' || state.step === 'applying')
      return (
        <div className={styles['stack']} aria-busy="true">
          <p role="status">{t(`productImport.working_${state.step}`)}</p>
          <Skeleton height="120px" />
        </div>
      );
    if (state.step === 'mapping' && state.preview !== null)
      return (
        <MappingStep
          preview={state.preview}
          columns={state.columns}
          defaultLocale={defaultLocale}
          permissions={permissions}
          depots={depots.data?.items ?? []}
          locationId={state.locationId}
          onColumns={flow.setColumns}
          onLocation={flow.setLocation}
        />
      );
    if (state.step === 'report' && state.view?.report != null)
      return <ReportStep report={state.view.report} locale={locale} />;
    if (state.step === 'done' && state.view !== null) return <ResultStep view={state.view} locale={locale} />;
    return (
      <div className={styles['stack']}>
        <FileDropZone
          accept={CSV_FILES}
          label={t('productImport.drop')}
          helperText={t('productImport.dropHelp')}
          onFile={flow.chooseFile}
        />
        <TemplateButton />
      </div>
    );
  };

  const footer = () => {
    if (state.step === 'mapping')
      return (
        <>
          <Button variant="ghost" onClick={flow.reset}>
            {t('productImport.otherFile')}
          </Button>
          <Button disabled={!canCheck(state.columns, defaultLocale, state.locationId)} onClick={flow.check}>
            {t('productImport.check')}
          </Button>
        </>
      );
    if (state.step === 'report')
      return (
        <>
          <Button variant="ghost" onClick={flow.reset}>
            {t('productImport.otherFile')}
          </Button>
          <Button
            icon={<Check size={20} aria-hidden="true" />}
            disabled={(state.view?.productCount ?? 0) === 0}
            onClick={flow.apply}
          >
            {t('productImport.apply', { count: String(state.view?.productCount ?? 0) })}
          </Button>
        </>
      );
    return (
      <Button variant={state.step === 'done' ? 'primary' : 'ghost'} onClick={onClose}>
        {t('common.close')}
      </Button>
    );
  };

  return (
    <Dialog
      open
      variant="side"
      title={t('productImport.title')}
      closeLabel={t('common.close')}
      onClose={onClose}
      footer={footer()}
    >
      <div className={styles['stack']}>
        {body()}
        {state.error === null ? null : (
          <p role="alert" className={styles['error']}>
            {state.error}
          </p>
        )}
      </div>
    </Dialog>
  );
};
